/**
 * API Client
 *
 * Fetch wrapper with error handling for Commander API
 */

import { ApiError, type ApiErrorResponse, type ProblemDetails } from './types/errors';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * Parse an error response body into an ApiError, understanding both the
 * `{ error, details }` shape and RFC-9110 problem-details
 * (`application/problem+json`, `{ type, title, status, detail }`). The
 * latter is what ASP.NET's `Results.Problem(...)` emits — its content
 * type is `application/problem+json`, which does NOT contain the substring
 * `application/json`, so it must be matched explicitly or the raw JSON
 * body leaks onto the screen as the error message.
 */
async function errorFromResponse(response: Response): Promise<ApiError> {
  const contentType = response.headers.get('content-type') ?? '';
  const isProblem = contentType.includes('application/problem+json');
  const isJson = isProblem || contentType.includes('application/json');

  if (isJson) {
    const body = (await response.json()) as ApiErrorResponse & ProblemDetails;
    // Prefer the problem-details shape when the human message lives in
    // `detail` / `title`; otherwise fall back to the `{ error }` shape.
    if (isProblem || body.error === undefined) {
      return ApiError.fromProblem(response.status, body);
    }
    return ApiError.fromResponse(response.status, body);
  }

  return new ApiError(response.status, await response.text());
}

let tokenProvider: (() => string | undefined) | null = null;

/**
 * Set a function that returns the current auth token.
 * Called by the admin app's auth provider on initialisation.
 */
export function setTokenProvider(provider: () => string | undefined): void {
  tokenProvider = provider;
}

/** The current auth token from the registered provider, if any. */
export function getToken(): string | undefined {
  return tokenProvider?.();
}

interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

/**
 * Make an API request with automatic JSON handling and error parsing
 */
export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;
  const token = tokenProvider?.();

  const response = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  // Handle no-content responses
  if (response.status === 204) {
    return undefined as T;
  }

  // Parse response body
  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');

  if (!response.ok) {
    throw await errorFromResponse(response);
  }

  if (isJson) {
    return response.json() as Promise<T>;
  }

  return response.text() as unknown as T;
}

/**
 * Trigger a browser download of an authenticated endpoint that returns
 * a non-JSON body (e.g. CSV, PDF). Buffers the response in a Blob
 * before kicking off the download — see docs/components/audit/
 * implementation.md for the trade-off vs. streaming via <a href>.
 */
export async function downloadFile(
  path: string,
  filename: string,
  options: Omit<RequestInit, 'body'> = {},
): Promise<void> {
  const token = tokenProvider?.();
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw await errorFromResponse(response);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Build query string from filter object
 */
export function buildQueryString<T extends object>(params: T): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Upload a file via multipart/form-data
 */
export async function uploadFile<T>(
  path: string,
  file: File,
  fieldName = 'file'
): Promise<T> {
  const formData = new FormData();
  formData.append(fieldName, file);

  const token = tokenProvider?.();

  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    body: formData,
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  });

  if (!response.ok) {
    throw await errorFromResponse(response);
  }

  return response.json() as Promise<T>;
}
